import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Pagination, 
  Card, 
  CardContent,
  Divider
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface PreguntaRespuestaApi {
  texto: string;
  tipo: string;
  orden: number;
}

interface QuestionsAndAnswersProps {
  preguntas_y_respuestas?: PreguntaRespuestaApi[];
  entityType?: 'licitaciones' | 'compras-agiles';
}

const ITEMS_PER_PAGE = 10;

const QuestionsAndAnswers: React.FC<QuestionsAndAnswersProps> = ({ 
  preguntas_y_respuestas = [], 
  entityType = 'licitaciones' 
}) => {
  const [page, setPage] = useState(1);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const preguntasAgrupadas: { pregunta: string; respuesta: string | null; orden: number }[] = [];
  
  if (preguntas_y_respuestas.length > 0) {
    const sortedItems = [...preguntas_y_respuestas].sort((a, b) => a.orden - b.orden);
    
    for (let i = 0; i < sortedItems.length; i++) {
      const current = sortedItems[i];
      
      if (current.tipo.toLowerCase().includes('pregunta')) {
        const next = sortedItems[i + 1];
        
        const respuesta = (next && 
                         next.tipo.toLowerCase().includes('respuesta') && 
                         next.orden === current.orden + 1) 
                         ? next.texto 
                         : null;
        
        preguntasAgrupadas.push({
          pregunta: current.texto,
          respuesta: respuesta,
          orden: current.orden
        });
      }
    }
  }

  if (preguntasAgrupadas.length === 0) {
    return (
      <Box sx={{ 
        width: '100%', 
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}>
        <HelpOutlineIcon sx={{ fontSize: 48, color: '#9E9E9E', mb: 2 }} />
        <Typography 
          sx={{ 
            color: '#9E9E9E', 
            fontSize: { xs: '14px', sm: '16px' },
            textAlign: 'center'
          }}
        >
          No hay preguntas y respuestas asociadas a esta {entityType === 'compras-agiles' ? 'compra ágil' : 'licitación'}
        </Typography>
      </Box>
    );
  }

  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const currentItems = preguntasAgrupadas.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {currentItems.map((item, index) => (
        <Card key={`${item.orden}-${index}`} sx={{ mb: 2, boxShadow: 'none', border: '1px solid #E0E0E0' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#585858' }}>
              Pregunta:
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: '#585858' }}>
              {item.pregunta}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#585858' }}>
              Respuesta:
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: item.respuesta ? '#585858' : '#9E9E9E',
                fontStyle: item.respuesta ? 'normal' : 'italic'
              }}
            >
              {item.respuesta || 'Sin respuesta aún'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#9E9E9E' }}>
              Orden: {item.orden}
            </Typography>
          </CardContent>
        </Card>
      ))}
      
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination 
          count={Math.ceil(preguntasAgrupadas.length / ITEMS_PER_PAGE)} 
          page={page} 
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default QuestionsAndAnswers; 